import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, CheckCircle, XCircle, Loader2, AlertCircle, Download, Eye } from 'lucide-react';
import { DataGrid } from '@t-one-internal/design-system/lib/components/ui/data-grid';
import { DateTimeDisplay, getTimezoneAbbr, useTimezone } from '@t-one-internal/design-system';
import type { ColumnDef } from '@tanstack/react-table';
import { customReportsApi } from '../../api/customReportsApi';
import { getReportDownloadUrl } from '../../api/downloadApi';
import type { CustomReportApiResponse, ReportRunHistory } from './custom-builder/types';

interface ReportHistoryProps {
    report: CustomReportApiResponse;
    onBack: () => void;
}

const ReportHistory: React.FC<ReportHistoryProps> = ({ report, onBack }) => {
    const navigate = useNavigate();
    const [history, setHistory] = useState<ReportRunHistory[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [downloadingId, setDownloadingId] = useState<string | null>(null);

    const loadHistory = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await customReportsApi.getReportHistory(report.id);
            setHistory(result.data);
        } catch (err) {
            console.error('Failed to load report history:', err);
            setError('Failed to load report history.');
        } finally {
            setLoading(false);
        }
    }, [report.id]);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    const handleViewReport = (reportId: string) => {
        navigate(`/analytics/reports/view/${reportId}`);
    };

    const handleDownload = async (generatedReportId: string) => {
        try {
            setDownloadingId(generatedReportId);
            const response = await getReportDownloadUrl(Number(generatedReportId));

            if (!response.success || !response.download_url) {
                alert(response.error || 'Failed to get download URL');
                return;
            }

            window.open(response.download_url, '_blank');
        } catch (err) {
            console.error('Failed to download report:', err);
            alert('Failed to download report. Please try again.');
        } finally {
            setDownloadingId(null);
        }
    };

    const getStatusIcon = (status: ReportRunHistory['status']) => {
        switch (status) {
            case 'Completed':
                return <CheckCircle size={16} className="text-green-500" />;
            case 'Failed':
                return <XCircle size={16} className="text-red-500" />;
            case 'Running':
                return <Loader2 size={16} className="text-blue-500 animate-spin" />;
            case 'Pending':
                return <Clock size={16} className="text-yellow-500" />;
            default:
                return <AlertCircle size={16} className="text-gray-400" />;
        }
    };

    const getStatusBadgeClass = (status: ReportRunHistory['status']) => {
        switch (status) {
            case 'Completed':
                return 'bg-green-100 text-green-800';
            case 'Failed':
                return 'bg-red-100 text-red-800';
            case 'Running':
                return 'bg-blue-100 text-blue-800';
            case 'Pending':
                return 'bg-yellow-100 text-yellow-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const { timezone } = useTimezone();
    const tzAbbr = getTimezoneAbbr(timezone);

    const formatDate = (dateString: string) => {
        const formatted = DateTimeDisplay.toStandardDateTimeInZone(dateString, timezone);
        return `${formatted} ${tzAbbr}`;
    };

    const columns: ColumnDef<ReportRunHistory>[] = [
        {
            accessorKey: 'created_at',
            header: 'Run Time',
            enableColumnFilter: false,
            enableSorting: true,
            size: 200,
            cell: ({ getValue }) => (
                <span className="text-sm text-gray-900">
                    {formatDate(getValue() as string)}
                </span>
            )
        },
        {
            accessorKey: 'status',
            header: 'Status',
            enableColumnFilter: false,
            enableSorting: true,
            size: 150,
            cell: ({ row }) => {
                const run = row.original;
                return (
                    <div>
                        <div className="flex items-center gap-2">
                            {getStatusIcon(run.status)}
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadgeClass(run.status)}`}>
                                {run.status}
                            </span>
                        </div>
                        {run.comments && (
                            <p className="mt-1 text-xs text-gray-600 truncate max-w-xs" title={run.comments}>
                                {run.comments}
                            </p>
                        )}
                    </div>
                );
            }
        },
        {
            accessorKey: 'actions',
            header: 'Actions',
            enableColumnFilter: false,
            enableSorting: false,
            size: 150,
            cell: ({ row }) => {
                const run = row.original;
                const isCompleted = run.status === 'Completed';
                return (
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => handleViewReport(run.id)}
                            className="p-1.5 rounded-md text-blue-600 hover:bg-blue-50 transition-colors"
                            title="View Report"
                        >
                            <Eye size={16} />
                        </button>
                        {isCompleted && (
                            <button
                                onClick={() => handleDownload(run.id)}
                                disabled={downloadingId === run.id}
                                className="p-1.5 rounded-md text-green-600 hover:bg-green-50 transition-colors disabled:opacity-50"
                                title="Download Excel"
                            >
                                {downloadingId === run.id ? (
                                    <Loader2 size={16} className="animate-spin" />
                                ) : (
                                    <Download size={16} />
                                )}
                            </button>
                        )}
                    </div>
                );
            }
        }
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                <span className="ml-2 text-sm text-gray-500">Loading history...</span>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-white">
            {/* Header Toolbar */}
            <header className="flex-none bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={onBack}
                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold text-gray-900">{report.name}</h1>
                        <p className="text-xs text-gray-500">
                            {report.report_type_name} | Generated Reports History
                        </p>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <div className="flex-1 overflow-hidden px-6 py-6">
                {error ? (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                        {error}
                    </div>
                ) : history.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                        <Clock size={48} className="mb-4 text-gray-300" />
                        <p className="text-sm">No run history available for this report.</p>
                        <p className="text-xs text-gray-400 mt-2">Generated reports will appear here after scheduled runs.</p>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm h-full flex flex-col overflow-hidden">
                        <div className="flex-1 overflow-hidden">
                            <DataGrid
                                data={history}
                                columns={columns}
                                showToolbar={false}
                                enableRowSelection={false}
                                enableSorting={true}
                                showPagination={true}
                                defaultPageSize={10}
                                rowDensity="normal"
                                fullHeight={true}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReportHistory;
